const tickRate = 500; // ticket every 500 ms

export interface TickEvent {
    secondsLeft: number;
}

// see https://codereview.stackexchange.com/questions/215161/a-countdown-clock-in-typescript
interface CountdownEvents {
    tick(values: TickEvent): void;
    expired(): void;
}

type EventMap<T> = { [K in keyof T]: Function[] };

interface IOnTick { (evt: TickEvent): void }
interface IOnComplete { (evt: TickEvent): void }

// Wrote my own timer because I was tired of non-typescript compatible timers.
export class CountdownTimer {
    private listeners: EventMap<CountdownEvents> = {
        tick: [],
        expired: []
    };
    private timer: number; // pointer to internal
    private seconds: number // # of seconds
    private startTimeMS: number;
    private endTimeMS: number;
    isRunning: boolean;

    constructor() {
        this.isRunning = false;
    }

    startTimer(seconds: number) {
        const self = this;
        this.seconds = seconds; // keep this around... no reason
        if (this.isRunning) {
            this.endTimer();
        }

        const d = new Date();
        this.startTimeMS = d.getTime();
        this.endTimeMS = this.startTimeMS + (seconds * 1000);

        this.timer = window.setInterval(() => self.tick(), tickRate);
    }

    // Force the timer to end
    endTimer() {
        if (this.timer) {
            window.clearInterval(this.timer);
        }
        this.isRunning = false;
    }

    tick() {
        var secondsLeft = this.getSecondsLeft();
        this.listeners.tick.forEach(listener => listener({ secondsLeft: secondsLeft }));
        if (secondsLeft <= 0) {
            this.endTimer();
            this.listeners.expired.forEach(listener => listener());
        }
    }

    // Watch an event
    on<K extends keyof CountdownEvents>(eventName: K, listener: CountdownEvents[K]): void {
        this.listeners[eventName].push(listener);
    }

    // Watch an event
    off<K extends keyof CountdownEvents>(eventName: K, listener: CountdownEvents[K]): void {
        const listeners = this.listeners[eventName];
        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
    }

    getFormattedTimeLeft(): string {
        return CountdownTimer.getFormattedTime(this.getSecondsLeft());
    }

    // get # of ms left
    getMSLeft(): number {
        const d = new Date();
        const timeLeft = this.endTimeMS - d.getTime();
        return timeLeft > 0 ? timeLeft : 0;
    }

    // get # of seconds left
    getSecondsLeft() {
        return Math.round(this.getMSLeft() / 1000);
    }

    getMSElapsed(): number{
        const d = new Date();
        const timeElapsed = d.getTime() - this.startTimeMS;
        const maxMS = this.seconds * 1000;
        return timeElapsed > maxMS ? maxMS : timeElapsed;
    }

    getSecondsElapsed(): number {
        return Math.round(this.getMSElapsed() / 1000);
    }

    static padtime(num: number): string {
        return num < 10 ? "0" + num : num.toString();
    }

    // Returns a nicely formatted time
    static getFormattedTime(sec_num: number): string {
        const hours = Math.floor(sec_num / 3600);
        const minutes = Math.floor((sec_num - (hours * 3600)) / 60);
        const seconds = sec_num - (hours * 3600) - (minutes * 60);

        const sSeconds = CountdownTimer.padtime(seconds);

        if (hours === 0) {
            // just min:sec
            return minutes + ":" + sSeconds;
        }

        const sMinutes = CountdownTimer.padtime(minutes);

        return hours + ':' + sMinutes + ':' + sSeconds;
    }
}